import React, { createContext, useContext, useState, useEffect } from 'react';
import { gaAddToCart } from '../utils/analytics';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Buy-10-get-1-free / buy-20-get-3-free bonus (individual loose shrimp only)
// 10 shrimp → +1 free, 20 shrimp → +3 free
export const calculateBonus = (quantity) => {
  if (quantity >= 20) return 3;
  if (quantity >= 10) return 1;
  return 0;
};

// The free-shrimp bonus only applies to individual loose shrimp (livestock).
// Colony/bundle packs (exclude_from_bonus) and non-livestock items (jars, simple
// items) are never eligible.
const isBonusEligible = (item) => {
  const type = item.product_type;
  const isLivestock = !type || type === 'livestock';
  return isLivestock && !item.exclude_from_bonus;
};

const bonusFor = (item, quantity) => (isBonusEligible(item) ? calculateBonus(quantity) : 0);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('shrimp-cart');
    if (!saved) return [];
    // Recompute bonus on load so eligibility rules always apply to saved carts
    return JSON.parse(saved).map(item => ({
      ...item,
      bonus_quantity: bonusFor(item, item.quantity || 0)
    }));
  });

  useEffect(() => {
    localStorage.setItem('shrimp-cart', JSON.stringify(items));
  }, [items]);

  // effectiveDiscount: { percent, name, isStoreWide } - optional, used for store-wide discount
  const addItem = (shrimp, quantity = 1, effectiveDiscount = null) => {
    setItems(prev => {
      // Calculate price per item (applying discount if any)
      let basePrice = parseFloat(shrimp.price) || 0;
      let unitPrice = basePrice;
      let appliedDiscount = null;
      let appliedDiscountName = null;

      // Item's own discount takes priority
      if (shrimp.discount_percent && shrimp.discount_percent > 0) {
        unitPrice = unitPrice * (1 - shrimp.discount_percent / 100);
        appliedDiscount = shrimp.discount_percent;
        appliedDiscountName = shrimp.discount_name;
      }
      // Otherwise apply store-wide discount if provided and item not excluded
      else if (effectiveDiscount && !shrimp.exclude_from_store_discount) {
        unitPrice = unitPrice * (1 - effectiveDiscount.percent / 100);
        appliedDiscount = effectiveDiscount.percent;
        appliedDiscountName = effectiveDiscount.name || 'Store Sale';
      }

      // Ensure unitPrice is never NaN
      if (isNaN(unitPrice) || unitPrice === null || unitPrice === undefined) {
        console.error('Invalid unit price for shrimp:', shrimp.name, 'basePrice:', basePrice);
        unitPrice = 0;
      }
      unitPrice = Math.round(unitPrice * 100) / 100;

      // For items with grade selection, use a unique ID that includes the grade
      const cartItemId = shrimp.selected_grade
        ? `${shrimp.id}_${shrimp.selected_grade}`
        : shrimp.id;

      const productType = shrimp.ad_type || 'livestock';
      const excludeFromBonus = !!shrimp.exclude_from_bonus;
      const bonusMeta = { product_type: productType, exclude_from_bonus: excludeFromBonus };

      const existing = prev.find(item => item.cart_item_id === cartItemId ||
        (!item.cart_item_id && !shrimp.selected_grade && item.shrimp_id === shrimp.id));

      if (existing) {
        const newQuantity = existing.quantity + quantity;
        return prev.map(item =>
          (item.cart_item_id === cartItemId ||
           (!item.cart_item_id && !shrimp.selected_grade && item.shrimp_id === shrimp.id))
            ? {
                ...item,
                quantity: newQuantity,
                product_type: productType,
                exclude_from_bonus: excludeFromBonus,
                bonus_quantity: bonusFor(bonusMeta, newQuantity),
                total_price: unitPrice * newQuantity,
                unit_price: unitPrice,
                discount_percent: appliedDiscount,
                discount_name: appliedDiscountName
              }
            : item
        );
      }

      return [
        ...prev,
        {
          cart_item_id: cartItemId,
          shrimp_id: shrimp.id,
          shrimp_name: shrimp.name,
          selected_grade: shrimp.selected_grade || null,
          product_type: productType,
          exclude_from_bonus: excludeFromBonus,
          quantity: quantity,
          bonus_quantity: bonusFor(bonusMeta, quantity),
          unit_price: unitPrice,
          original_price: shrimp.price,
          discount_percent: appliedDiscount,
          discount_name: appliedDiscountName,
          total_price: unitPrice * quantity,
          image: shrimp.images?.[0] || shrimp.image_url
        }
      ];
    });
    // GA4 ecommerce signal (no-op for admins / non-prod)
    let gaPrice = parseFloat(shrimp.price) || 0;
    if (shrimp.discount_percent > 0) gaPrice *= (1 - shrimp.discount_percent / 100);
    else if (effectiveDiscount && !shrimp.exclude_from_store_discount) gaPrice *= (1 - effectiveDiscount.percent / 100);
    gaAddToCart({ shrimp_id: shrimp.id, shrimp_name: shrimp.name, unit_price: gaPrice, quantity, selected_grade: shrimp.selected_grade });
  };

  const removeItem = (cartItemIdOrShrimpId) => {
    setItems(prev => prev.filter(item =>
      item.cart_item_id !== cartItemIdOrShrimpId && item.shrimp_id !== cartItemIdOrShrimpId
    ));
  };

  const updateQuantity = (cartItemIdOrShrimpId, quantity) => {
    if (quantity <= 0) {
      removeItem(cartItemIdOrShrimpId);
      return;
    }
    setItems(prev =>
      prev.map(item => {
        if (item.cart_item_id === cartItemIdOrShrimpId || item.shrimp_id === cartItemIdOrShrimpId) {
          const unitPrice = parseFloat(item.unit_price) || 0;
          return {
            ...item,
            quantity,
            bonus_quantity: bonusFor(item, quantity),
            total_price: isNaN(unitPrice * quantity) ? 0 : unitPrice * quantity
          };
        }
        return item;
      })
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const totalItems = items.reduce((acc, item) => acc + (item.quantity || 0), 0);
  const totalBonusItems = items.reduce((acc, item) => acc + (item.bonus_quantity || 0), 0);
  // Quantity of bonus-eligible individual shrimp (drives the "add X more" cart nudge)
  const eligibleBonusQuantity = items.reduce(
    (acc, item) => acc + (isBonusEligible(item) ? (item.quantity || 0) : 0), 0
  );
  const totalPrice = items.reduce((acc, item) => {
    const itemTotal = parseFloat(item.total_price) || 0;
    return acc + (isNaN(itemTotal) ? 0 : itemTotal);
  }, 0);

  // Add a jar with configuration to cart
  const addJarItem = (jar, jarConfig, totalPrice) => {
    setItems(prev => {
      // Jars are always unique entries (each configuration is different)
      const cartItemId = `jar_${jar.id}_${Date.now()}`;

      return [
        ...prev,
        {
          cart_item_id: cartItemId,
          shrimp_id: jar.id,
          shrimp_name: jar.name,
          product_type: 'jar',
          quantity: 1,
          bonus_quantity: 0, // No bonus for jars
          unit_price: totalPrice,
          total_price: totalPrice,
          jar_config: jarConfig,
          image: jar.images?.[0] || null
        }
      ];
    });
    // GA4 ecommerce signal (no-op for admins / non-prod)
    gaAddToCart({ shrimp_id: jar.id, shrimp_name: jar.name, unit_price: totalPrice, quantity: 1 });
  };

  // Add a simple item to cart (with optional size variant)
  const addSimpleItem = (item, quantity = 1, selectedSize = null, effectiveDiscount = null) => {
    setItems(prev => {
      const cheapestSize = (item.sizes || item.size_variants || [])
        .map(s => parseFloat(s.price)).filter(p => !isNaN(p) && p > 0);
      const selectedSizePrice = selectedSize ? parseFloat(selectedSize.price) : NaN;
      // Size-only products keep their price in sizes[] (top-level price is null);
      // never let a missing/blank size zero-out the price — floor at the cheapest size.
      let basePrice = selectedSizePrice;
      if (isNaN(basePrice) || basePrice <= 0) {
        const top = parseFloat(item.price);
        basePrice = (!isNaN(top) && top > 0) ? top : (cheapestSize.length ? Math.min(...cheapestSize) : 0);
      }
      let unitPrice = basePrice;
      let appliedDiscount = null;
      let appliedDiscountName = null;

      // Item's own discount takes priority
      if (item.discount_percent && item.discount_percent > 0) {
        unitPrice = unitPrice * (1 - item.discount_percent / 100);
        appliedDiscount = item.discount_percent;
        appliedDiscountName = item.discount_name;
      }
      // Otherwise apply store-wide discount if provided and item not excluded
      else if (effectiveDiscount && !item.exclude_from_store_discount) {
        unitPrice = unitPrice * (1 - effectiveDiscount.percent / 100);
        appliedDiscount = effectiveDiscount.percent;
        appliedDiscountName = effectiveDiscount.name || 'Store Sale';
      }

      if (isNaN(unitPrice)) unitPrice = 0;

      const sizeSuffix = selectedSize ? `_size_${selectedSize.name}` : '';
      const cartItemId = `simple_${item.id}${sizeSuffix}`;
      const displayName = selectedSize ? `${item.name} (${selectedSize.name})` : item.name;

      const existing = prev.find(i => i.cart_item_id === cartItemId);
      if (existing) {
        const newQuantity = existing.quantity + quantity;
        return prev.map(i =>
          i.cart_item_id === cartItemId
            ? {
                ...i,
                quantity: newQuantity,
                bonus_quantity: 0,
                total_price: unitPrice * newQuantity,
                unit_price: unitPrice,
                discount_percent: appliedDiscount,
                discount_name: appliedDiscountName
              }
            : i
        );
      }

      return [
        ...prev,
        {
          cart_item_id: cartItemId,
          shrimp_id: item.id,
          shrimp_name: displayName,
          product_type: 'simple',
          selected_size: selectedSize?.name || null,
          quantity,
          bonus_quantity: 0,
          unit_price: unitPrice,
          original_price: basePrice,
          discount_percent: appliedDiscount,
          discount_name: appliedDiscountName,
          total_price: unitPrice * quantity,
          image: item.images?.[0] || item.image_url
        }
      ];
    });
    // GA4 ecommerce signal (no-op for admins / non-prod)
    let gaPrice = selectedSize ? (parseFloat(selectedSize.price) || 0) : (parseFloat(item.price) || 0);
    if (item.discount_percent > 0) gaPrice *= (1 - item.discount_percent / 100);
    else if (effectiveDiscount && !item.exclude_from_store_discount) gaPrice *= (1 - effectiveDiscount.percent / 100);
    gaAddToCart({ shrimp_id: item.id, shrimp_name: selectedSize ? `${item.name} (${selectedSize.name})` : item.name, unit_price: gaPrice, quantity });
  };

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        addJarItem,
        addSimpleItem,
        removeItem,
        updateQuantity,
        clearCart,
        totalItems,
        totalBonusItems,
        eligibleBonusQuantity,
        totalPrice,
        calculateBonus
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
